import type { UserLocalStorage } from '../../../types/localStorage';

import TagManager from 'react-gtm-module';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { twMerge } from 'tailwind-merge';
import useLocalStorageState from 'use-local-storage-state';

import { getEnv } from '../../../lib/env';
import { markTaskAsDone } from '../../../lib/flotiq-client/api-helpers';
import { generateApiDocs, getTestProps } from '../../../lib/helpers';

import LinkButton from '../../../components/LinkButton/LinkButton';

import { useApiKeys } from '../../../hooks/api';
import useDarkMode from '../../../hooks/useDarkMode';
import useSelectedSpace from '../../../hooks/useSelectedSpace';
import useToken from '../../../hooks/useToken';

import addContentObjectImageDark from '../../../images/graphics/onboarding/add-content-object-dark.png';
import addContentObjectImage from '../../../images/graphics/onboarding/add-content-object.png';
import addContentTypeImageDark from '../../../images/graphics/onboarding/add-content-type-dark.png';
import addContentTypeImage from '../../../images/graphics/onboarding/add-content-type.png';
import apiDocsImageDark from '../../../images/graphics/onboarding/api-docs-dark.png';
import apiDocsImage from '../../../images/graphics/onboarding/api-docs.png';
import docsImageDark from '../../../images/graphics/onboarding/docs-dark.png';
import docsImage from '../../../images/graphics/onboarding/docs.png';
import {
  ArrowRightIcon,
  CaretRightIcon,
  CheckmarkIcon,
  PlusIcon,
} from '../../../images/shapes';

import { DEFINED_TYPES } from '../../ContentTypeDefinitions/definedTypes';

const APIKEY_PARAMS = {
  global: true,
  order_by: 'created_at',
  order_direction: 'asc',
};

const DashboardFirstSteps = ({ testId = '' }: { testId?: string }) => {
  const { t } = useTranslation();
  const jwt = useToken();
  const [darkMode] = useDarkMode();
  const { buildUrlWithSpace } = useSelectedSpace();
  const { data: apiKeysData } = useApiKeys(APIKEY_PARAMS);

  const [userStorage, setUserStorage] =
    useLocalStorageState<UserLocalStorage>('cms.user');

  const toDoList = userStorage?.data?.config?.todo;
  const isDeveloper = userStorage?.data?.workType === 'Developer';

  const contentTypesToAdd = [
    ...Object.values(DEFINED_TYPES).map((type) => ({
      name: type.name,
      label: type.label,
      link: buildUrlWithSpace(
        `content-type-definitions/add?defaultType=${type.name}`,
      ),
    })),
    {
      name: 'custom',
      label: t('ContentDefinition.Custom'),
      link: buildUrlWithSpace('content-type-definitions/add'),
    },
  ];

  const tasks = [
    {
      key: 'content-type',
      label: t('Home.CreateYourContentType'),
      description: t('Home.CreateYourContentTypeDescription'),
      done: toDoList?.contentType?.status === 'done',
      links: (
        <ul className="w-full grid grid-cols-3 xl:grid-cols-6 gap-4">
          {contentTypesToAdd.map((type) => (
            <li key={type.name}>
              <Link
                className={twMerge(
                  'w-full text-base h-10 bg-slate-50 hover:bg-blue-50 rounded-lg',
                  'flex items-center justify-center gap-2 transition-colors',
                  'dark:bg-slate-800 dark:hover:bg-blue-900',
                )}
                to={type.link}
                {...getTestProps(testId, `add-${type.name}-link`)}
              >
                <span
                  className="bg-blue-200 dark:bg-blue p-1.5 rounded-full flex items-center justify-center"
                  aria-hidden="true"
                >
                  <PlusIcon className="size-2 text-blue-600 dark:text-slate-200" />
                </span>
                {type.label}
              </Link>
            </li>
          ))}
        </ul>
      ),
      image: addContentTypeImage,
      imageDark: addContentTypeImageDark,
    },
    {
      key: 'content-object',
      label: t('Home.CreateContentObject'),
      description: t('Home.CreateContentObjectDescription'),
      done: toDoList?.contentObject?.status === 'done',
      links: (
        <LinkButton
          buttonColor="borderlessBlue"
          link={
            toDoList?.contentType?.contentTypeSlug
              ? buildUrlWithSpace(
                  `content-type-objects/${toDoList?.contentType?.contentTypeSlug}`,
                )
              : buildUrlWithSpace('content-type-definitions')
          }
          iconImage={<CaretRightIcon className="size-2.5" />}
          additionalClasses="hover:opacity-100 hover:underline"
          {...getTestProps(testId, `add-content-object-link`)}
        >
          {t('Home.AddContentObject')}
        </LinkButton>
      ),
      image: addContentObjectImage,
      imageDark: addContentObjectImageDark,
    },
    {
      key: 'visit-docs',
      label: t('Home.LearnHowToUseContent'),
      description: isDeveloper
        ? t('Home.LearnHowToUseContentApiDoc')
        : t('Home.LearnHowToUseContentDocumentation'),
      done: toDoList?.publicDoc?.status === 'done',
      links: (
        <LinkButton
          buttonColor="borderlessBlue"
          link={
            isDeveloper
              ? generateApiDocs(
                  userStorage?.data,
                  apiKeysData?.find(
                    (key) => key.name.toLowerCase() === 'read only api key',
                  ),
                )
              : getEnv('VITE_DOCUMENTATION')
          }
          iconImage={<CaretRightIcon className="size-2.5" />}
          additionalClasses="hover:opacity-100 hover:underline"
          onClick={() => {
            markTaskAsDone('publicDoc', userStorage, jwt, setUserStorage);
            if (isDeveloper) {
              TagManager.dataLayer({
                dataLayer: {
                  event: 'apidocs_visit',
                },
              });
            }
          }}
          target={'_blank'}
          rel={'noopener noreferrer'}
          {...getTestProps(testId, `visit-docs-link`)}
        >
          {t('Home.VisitDocumentation')}
        </LinkButton>
      ),
      image: isDeveloper ? apiDocsImage : docsImage,
      imageDark: isDeveloper ? apiDocsImageDark : docsImageDark,
    },
  ];

  const currentTask = tasks.find((task) => !task.done);

  if (!currentTask) return null;

  return (
    <section
      id="dashboard-section-first-steps"
      {...getTestProps(testId, 'first-steps')}
    >
      <h2
        id="dashboard-first-steps-heading"
        className="text-xl mb-2 dark:text-slate-200"
      >
        {t('Home.FirstSteps')}
      </h2>
      <div className="grid md:grid-cols-[1fr_250px] 2xl:grid-cols-[1fr_480px] gap-4">
        <ol className="space-y-4">
          {tasks.map((task, index) => {
            const isCurrentTask = task.key === currentTask?.key;
            return (
              <li
                key={task.key}
                className={twMerge(
                  'rounded-lg flex flex-col p-3 rounded-lg border',
                  task.done &&
                    'bg-lime-50 dark:bg-lime-950 border-lime-400 dark:border-lime-800 dark:text-slate-200',
                  isCurrentTask &&
                    'bg-white dark:bg-slate-950 border-blue-300 dark:border-blue-800 dark:text-slate-200',
                  !task.done &&
                    !isCurrentTask &&
                    'bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 dark:text-slate-400',
                )}
                {...getTestProps(testId, task.key)}
              >
                <div className="flex items-center justify-between gap-4">
                  <h3 className="text-lg ml-1 font-medium">
                    <span aria-hidden="true">{index + 1}. </span>
                    {task.label}
                  </h3>
                  {isCurrentTask && (
                    <ArrowRightIcon
                      className="shrink-0 w-3 h-auto text-blue dark:text-slate-200"
                      {...getTestProps(testId, `${task.key}-arrow-icon`)}
                    />
                  )}
                  {task.done && (
                    <CheckmarkIcon
                      className="shrink-0 w-3 h-auto text-lime-700 dark:text-lime-600"
                      {...getTestProps(testId, `${task.key}-check-icon`)}
                    />
                  )}
                </div>
                {isCurrentTask && (
                  <>
                    <p
                      className="text-base mt-1.5"
                      {...getTestProps(testId, `${task.key}-description`)}
                    >
                      {task.description}
                    </p>
                    {task.links && (
                      <div className="w-full mt-3">{task.links}</div>
                    )}
                  </>
                )}
              </li>
            );
          })}
        </ol>
        {currentTask && (
          <img
            src={darkMode ? currentTask.imageDark : currentTask.image}
            alt={currentTask.label}
            className={
              'h-fit w-full md:h-0 md:min-h-full rounded-lg object-cover border border-slate-300 dark:border-slate-700'
            }
          />
        )}
      </div>
    </section>
  );
};

export default DashboardFirstSteps;
